/**
 * Planned vs actual (5.3) on the KPI page: planned vs observed duration by visit
 * type, planned vs actual drive, and the duration recalibration a person turns
 * into configuration. A small build; nothing auto-applies.
 *
 * The recalibration line belongs to **durations only**. The points column is
 * configuration read back, with the observed all-in time beside it as
 * information: the branch's point values are theirs and they are pay, so nothing
 * on this card proposes one.
 */
import { useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { RECALIBRATION_THRESHOLD, selectPlannedVsActual, type PlannedVsActualRow } from "../../engine/plannedVsActual";
import { useDemoSelector } from "../../store/useDemoStore";
import { useHomeHealthDemoEnabled } from "../../useHomeHealthDemoEnabled";

export function HomeHealthPlannedVsActualWidget() {
  const enabled = useHomeHealthDemoEnabled();
  const config = useDemoSelector((s) => s.config);
  const result = useMemo(() => selectPlannedVsActual(config), [config]);
  if (!enabled) return null;
  return (
    <Grid size={12} sx={{ mt: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Typography fontWeight={500}>Planned vs actual</Typography>
            <Typography variant="caption" color="text.secondary">
              Trailing {result.trailingWeeks} weeks · {result.recommendations} of {result.rows.length} visit types drifted past {Math.round(RECALIBRATION_THRESHOLD * 100)}%
            </Typography>
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ "& td, & th": { py: 0.5, whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Visit type</TableCell>
                  <TableCell align="right">Planned</TableCell>
                  <TableCell align="right">Observed (n)</TableCell>
                  <TableCell>Δ in-home</TableCell>
                  <TableCell align="right">Drive planned → actual</TableCell>
                  <TableCell align="right">Points (configured)</TableCell>
                  <TableCell>Recommendation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((r) => (
                  <Row key={r.kind} r={r} />
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

function pct(n: number): string {
  const v = Math.round(n * 100);
  return `${v > 0 ? "+" : ""}${v}%`;
}

function Row({ r }: { r: PlannedVsActualRow }) {
  const scale = Math.max(r.plannedInHomeMinutes, r.observedInHomeMinutes, 1) * 1.15;
  const w = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;
  const recommend = r.recommendedInHomeMinutes !== undefined;
  const observedAllIn = r.observedInHomeMinutes + r.observedDriveMinutes;
  return (
    <TableRow hover data-kind={r.kind} data-recommend={recommend}>
      <TableCell>{r.label}</TableCell>
      <TableCell align="right">{r.plannedInHomeMinutes} min</TableCell>
      <TableCell align="right">
        {r.observedInHomeMinutes} min <Typography component="span" variant="caption" color="text.secondary">({r.sampleSize})</Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 160 }}>
        <Tooltip title={`planned ${r.plannedInHomeMinutes} · observed ${r.observedInHomeMinutes}`}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ height: 5, width: w(r.plannedInHomeMinutes), bgcolor: "#9e9e9e", borderRadius: 1 }} />
            <Box sx={{ height: 5, width: w(r.observedInHomeMinutes), bgcolor: recommend ? "#e0a13a" : "#7aa6d0", borderRadius: 1 }} />
          </Box>
        </Tooltip>
        <Typography variant="caption" color={recommend ? "warning.main" : "text.secondary"}>
          {pct(r.durationDeltaPct)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ color: Math.abs(r.driveDeltaPct) >= 0.1 ? "warning.main" : undefined }}>
        {r.plannedDriveMinutes} → {r.observedDriveMinutes} min ({pct(r.driveDeltaPct)})
      </TableCell>
      <TableCell align="right">
        <Tooltip title={`configured ${r.points} points = ${r.pointImpliedAllInMinutes} min all-in; observed all-in ${observedAllIn} min (${pct(r.pointsGapPct)}). Information only — point values are the branch's.`}>
          <span>
            {r.points} pts{" "}
            <Typography component="span" variant="caption" color="text.secondary">
              ({r.pointImpliedAllInMinutes} min all-in · observed {observedAllIn} min)
            </Typography>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ whiteSpace: "normal", minWidth: 260 }}>
        <Typography variant="caption" color={recommend ? "text.primary" : "text.secondary"}>
          {r.recommendation}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
